import type { ReactNode } from 'react'
import type { CSSVars } from '@/types/css'

import { useEffect, useState } from 'react'
import { SiderealTime } from 'astronomy-engine'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { OBSERVATORY } from '@/constants/observatory'

interface DividerProps {
    className?: string
    label?: string
}

const tickStyle = (index: number): CSSVars => ({ '--i': index })

/** A wide graduated arc, like the limb of an astrolabe, drawn as it scrolls in. */
export const ArcDivider = ({ className, label }: DividerProps): ReactNode => {
    const { ref, inView } = useInView<HTMLDivElement>()
    const radius = 3000
    const cx = 600
    const cy = radius + 30
    const ticks: ReactNode[] = []
    const point = (angle: number, r: number) => {
        const rad = (angle * Math.PI) / 180

        return { x: cx + r * Math.sin(rad), y: cy - r * Math.cos(rad) }
    }

    let index = 0

    for (let step = -46; step <= 46; step++) {
        const angle = step * 0.25
        const isMajor = step % 20 === 0
        const isMid = step % 4 === 0
        const length = isMajor ? 15 : isMid ? 8 : 4
        const a = point(angle, radius)
        const b = point(angle, radius + length)

        ticks.push(
            <line
                key={step}
                className='tick'
                style={tickStyle(index++)}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke={isMajor ? 'var(--c-brass)' : 'var(--c-hair-2)'}
                strokeWidth={1}
                vectorEffect='non-scaling-stroke'
            />
        )

        if (isMajor && step !== 0) {
            const t = point(angle, radius + 26)

            ticks.push(
                <text
                    key={`label-${step}`}
                    className='tick hidden sm:block'
                    style={tickStyle(index)}
                    x={t.x}
                    y={t.y}
                    textAnchor='middle'
                    fill='var(--c-star-3)'
                    fontFamily='var(--font-app-mono, ui-monospace, monospace)'
                    fontSize='9'
                    letterSpacing='1.5'
                >
                    {`${angle > 0 ? '+' : '−'}${Math.abs(angle)}°`}
                </text>
            )
        }
    }

    const left = point(-11.6, radius)
    const right = point(11.6, radius)
    const innerLeft = point(-9, radius - 14)
    const innerRight = point(9, radius - 14)

    return (
        <div ref={ref} aria-hidden className={cn('relative w-full', inView && 'is-drawn', className)}>
            <svg viewBox='0 0 1200 110' className='block h-auto w-full overflow-visible' preserveAspectRatio='xMidYMax meet'>
                <path
                    className='draw'
                    pathLength={1}
                    d={`M ${left.x} ${left.y} A ${radius} ${radius} 0 0 1 ${right.x} ${right.y}`}
                    fill='none'
                    stroke='var(--c-hair-2)'
                    strokeWidth={1}
                />
                <path
                    className='tick'
                    style={{ '--draw-delay': '900ms' } as CSSVars}
                    d={`M ${innerLeft.x} ${innerLeft.y} A ${radius - 14} ${radius - 14} 0 0 1 ${innerRight.x} ${innerRight.y}`}
                    fill='none'
                    stroke='var(--c-hair)'
                    strokeWidth={1}
                    strokeDasharray='2 7'
                    vectorEffect='non-scaling-stroke'
                />
                {ticks}
                <circle className='tick' cx={cx} cy={30} r={3.2} fill='none' stroke='var(--c-brass)' strokeWidth={1} vectorEffect='non-scaling-stroke' />
                <line className='tick' x1={cx} y1={4} x2={cx} y2={24} stroke='var(--c-brass)' strokeWidth={1} vectorEffect='non-scaling-stroke' />
            </svg>

            {label && (
                <p className='label-mono absolute left-1/2 top-full mt-3 -translate-x-1/2 whitespace-nowrap text-star-3'>
                    {label}
                </p>
            )}
        </div>
    )
}

const formatSidereal = (hours: number): string => {
    const h = Math.floor(hours)
    const m = Math.floor((hours - h) * 60)

    return `${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m`
}

/** A right-ascension scale with a live marker for the local sidereal time. */
export const ScaleDivider = ({ className }: DividerProps): ReactNode => {
    const { ref, inView } = useInView<HTMLDivElement>()
    const [sidereal, setSidereal] = useState<number | null>(null)

    useEffect(() => {
        const update = () => {
            const gast = SiderealTime(new Date())
            const local = (((gast + OBSERVATORY.longitude / 15) % 24) + 24) % 24

            setSidereal(local)
        }

        update()
        const timer = window.setInterval(update, 30000)

        return () => window.clearInterval(timer)
    }, [])

    const ticks: ReactNode[] = []

    for (let step = 0; step <= 144; step++) {
        const x = (step / 144) * 1200
        const isHour = step % 6 === 0
        const isMajor = step % 12 === 0
        const length = isMajor ? 12 : isHour ? 7 : 3

        ticks.push(
            <line
                key={step}
                className='tick'
                style={tickStyle(step)}
                x1={x}
                y1={30}
                x2={x}
                y2={30 - length}
                stroke={isMajor ? 'var(--c-star-3)' : 'var(--c-hair-2)'}
                strokeWidth={1}
                vectorEffect='non-scaling-stroke'
            />
        )

        if (isMajor && step < 144) {
            ticks.push(
                <text
                    key={`l-${step}`}
                    className='tick hidden sm:block'
                    style={tickStyle(step)}
                    x={x + 4}
                    y={46}
                    fill='var(--c-star-3)'
                    fontFamily='var(--font-app-mono, ui-monospace, monospace)'
                    fontSize='9'
                    letterSpacing='1.5'
                >
                    {`${String(step / 6).padStart(2, '0')}h`}
                </text>
            )
        }
    }

    const markerX = sidereal === null ? null : (sidereal / 24) * 1200

    return (
        <div ref={ref} aria-hidden className={cn('relative w-full', inView && 'is-drawn', className)}>
            <svg viewBox='0 0 1200 56' className='block h-auto w-full overflow-visible'>
                <line className='draw' pathLength={1} x1={0} y1={30} x2={1200} y2={30} stroke='var(--c-hair-2)' strokeWidth={1} />
                {ticks}
                {markerX !== null && (
                    <g className='tick' style={tickStyle(80)}>
                        <path d={`M ${markerX} 30 L ${markerX - 4} 22 L ${markerX + 4} 22 Z`} fill='var(--c-brass)' />
                        <line x1={markerX} y1={30} x2={markerX} y2={52} stroke='var(--c-brass)' strokeWidth={1} vectorEffect='non-scaling-stroke' />
                        <text
                            className='hidden sm:block'
                            x={markerX + (markerX > 1080 ? -8 : 8)}
                            y={14}
                            textAnchor={markerX > 1080 ? 'end' : 'start'}
                            fill='var(--c-brass)'
                            fontFamily='var(--font-app-mono, ui-monospace, monospace)'
                            fontSize='9'
                            letterSpacing='1.5'
                        >
                            {`LOCAL SIDEREAL ${formatSidereal(sidereal ?? 0)}`}
                        </text>
                    </g>
                )}
            </svg>
        </div>
    )
}

/** Concentric partial rings around a crosshair, like a finder reticle. */
export const RingsDivider = ({ className, label }: DividerProps): ReactNode => {
    const { ref, inView } = useInView<HTMLDivElement>()
    const cx = 600
    const cy = 70
    const ticks: ReactNode[] = []
    const arc = (r: number, from: number, to: number) => {
        const a = ((from - 90) * Math.PI) / 180
        const b = ((to - 90) * Math.PI) / 180
        const large = to - from > 180 ? 1 : 0

        return `M ${cx + r * Math.cos(a)} ${cy + r * Math.sin(a)} A ${r} ${r} 0 ${large} 1 ${cx + r * Math.cos(b)} ${cy + r * Math.sin(b)}`
    }

    for (let deg = 0; deg < 360; deg += 5) {
        const rad = ((deg - 90) * Math.PI) / 180
        const inner = deg % 30 === 0 ? 58 : 62

        ticks.push(
            <line
                key={deg}
                className='tick'
                style={tickStyle(deg / 5)}
                x1={cx + inner * Math.cos(rad)}
                y1={cy + inner * Math.sin(rad)}
                x2={cx + 66 * Math.cos(rad)}
                y2={cy + 66 * Math.sin(rad)}
                stroke={deg % 90 === 0 ? 'var(--c-brass)' : 'var(--c-hair-2)'}
                strokeWidth={1}
                vectorEffect='non-scaling-stroke'
            />
        )
    }

    return (
        <div ref={ref} aria-hidden className={cn('relative w-full', inView && 'is-drawn', className)}>
            <svg viewBox='0 0 1200 140' className='block h-auto w-full overflow-visible'>
                <line className='draw' pathLength={1} x1={0} y1={cy} x2={500} y2={cy} stroke='var(--c-hair)' strokeWidth={1} />
                <line className='draw' pathLength={1} x1={1200} y1={cy} x2={700} y2={cy} stroke='var(--c-hair)' strokeWidth={1} />
                <path className='draw' pathLength={1} d={arc(66, 0, 359.9)} fill='none' stroke='var(--c-hair-2)' strokeWidth={1} />
                <path className='draw' style={{ '--draw-delay': '400ms' } as CSSVars} pathLength={1} d={arc(44, 200, 340)} fill='none' stroke='var(--c-brass-2)' strokeWidth={1} />
                <path className='draw' style={{ '--draw-delay': '700ms' } as CSSVars} pathLength={1} d={arc(44, 20, 160)} fill='none' stroke='var(--c-brass-2)' strokeWidth={1} />
                <path className='tick' style={{ '--draw-delay': '900ms' } as CSSVars} d={arc(24, 0, 359.9)} fill='none' stroke='var(--c-hair)' strokeWidth={1} strokeDasharray='2 4' vectorEffect='non-scaling-stroke' />
                {ticks}
                <line className='tick' x1={cx - 14} y1={cy} x2={cx - 5} y2={cy} stroke='var(--c-brass)' strokeWidth={1} vectorEffect='non-scaling-stroke' />
                <line className='tick' x1={cx + 5} y1={cy} x2={cx + 14} y2={cy} stroke='var(--c-brass)' strokeWidth={1} vectorEffect='non-scaling-stroke' />
                <line className='tick' x1={cx} y1={cy - 14} x2={cx} y2={cy - 5} stroke='var(--c-brass)' strokeWidth={1} vectorEffect='non-scaling-stroke' />
                <line className='tick' x1={cx} y1={cy + 5} x2={cx} y2={cy + 14} stroke='var(--c-brass)' strokeWidth={1} vectorEffect='non-scaling-stroke' />
            </svg>

            {label && (
                <p className='label-mono mt-2 text-center text-star-3'>
                    {label}
                </p>
            )}
        </div>
    )
}
